const HTML_ENTITIES = new Map([
  ['&', '&amp;'],
  ['<', '&lt;'],
  ['>', '&gt;'],
  ['"', '&quot;'],
  ['\'', '&#39;'],
]);

// ConfirmationModalService renders `content` with [innerHtml], and guest names come from public
// joins: escape them so a name can't add links or images to the host's prompt.
export function escapeHtml(text: string): string {
  return text.replace(/[&<>"']/gu, char => HTML_ENTITIES.get(char) ?? char);
}

// The backend drops overrideSeatedConflict before it saves the party. Put the flag back on the
// saved party, so the seat step after a floor-plan drag does not ask "Seat anyway" again.
export function keepGrantedSeatOverride<T extends object>(
  saved: T,
  request: { overrideSeatedConflict?: boolean },
): T & { overrideSeatedConflict?: boolean } {
  if (!request?.overrideSeatedConflict || !saved) {
    return saved;
  }

  return {
    ...saved,
    overrideSeatedConflict: true,
  };
}

export type SeatedConflictAction = 'Restore' | 'Save' | 'Seat';

export interface ConfirmPromptOptions {
  content: string;
  confirmBtnText: string;
  closeBtnText: string;
}

export function seatedConflictPrompt(
  conflictParties?: string[],
  action: SeatedConflictAction = 'Seat',
): string {
  const names = (conflictParties || []).filter(Boolean).map(escapeHtml);
  const who = names.length ? ` by ${names.join(', ')}` : '';

  return `This table is already in use${who} at that time. ${action} anyway?`;
}

export function seatedConflictConfirm(
  conflictParties?: string[],
  action: SeatedConflictAction = 'Seat',
): ConfirmPromptOptions {
  return {
    content: seatedConflictPrompt(conflictParties, action),
    confirmBtnText: `${action} anyway`,
    closeBtnText: 'Cancel',
  };
}

export function saveOrSeatAction(handOff: boolean): SeatedConflictAction {
  if (handOff) {
    return 'Seat';
  }

  return 'Save';
}

export function saveAnywayConfirm(warning: string): ConfirmPromptOptions {
  return {
    content: `${warning} Save anyway?`,
    confirmBtnText: 'Save anyway',
    closeBtnText: 'Cancel',
  };
}
